import { Component, OnInit } from '@angular/core';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DataIntervalTagEditDialog } from '@app/shared/edit-dialog/data-interval-tag/data-interval-tag-edit-dialog.component';
import { User } from '@models';
import { op, Subscription, firstValueFrom, take } from '@lib/rxjs';
import { NavigationRoute$ } from '@injectables';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DialogCsvOptionsComponent } from '../dialog-csv-options/dialog-csv-options';
import { CsvUtilService, MonthlyReportMonthOption } from 'src/injectables/csv-util.service';

@Component({
    selector: 'dialog-report-properties',
    templateUrl: './dialog-report-properties.component.html',
    styleUrls: ['./dialog-report-properties.component.scss'],
})
export class DialogReportPropertiesComponent implements OnInit {
    public particleSizeUnit: string;
    public weightTypeTitle: string;
    public totalPayload: number;
    private deviceName: string = '';
    public defaultFileType: 'single-row' | 'multiple-row' = 'single-row';
    public psdDataFormatInCSV: 'Default' | 'Single Row' | 'Multiple Rows' = 'Default';
    public monthlyCsvMonths: MonthlyReportMonthOption[] = [];
    public showMonthlyCsvMonths = false;
    public selectedMonthlyCsvMonth: string = null;
    public hasCurrentMonthCsv = false;

    constructor(
        private fragQuery: FragQueryService,
        private readonly host: MatDialogRef<DataIntervalTagEditDialog, boolean>,
        public user: User,
        private readonly route$: NavigationRoute$,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private readonly csvDownloadService: CsvUtilService

    ) { }

    ngOnInit() {
        this.fragQuery.exportReport = true;

        this.fragQuery.particleSizeUnitSystem$.subscribe((unit: string) => {
            this.particleSizeUnit = unit;
        }),
            this.fragQuery.QuantityTitle$.subscribe((title: string) => {
                this.weightTypeTitle = title;
            }),
            this.fragQuery.totalPayload$.subscribe((payload: number) => {
                this.totalPayload = payload;
            }),
            this.fragQuery.device$.subscribe(
                device => { this.deviceName = device.name }
            ),
            this.type$.subscribe((type) => { this.fileType = type });
    }

    public readonly begin$ = this.fragQuery.begin$;
    public readonly end$ = this.fragQuery.end$;
    public readonly device$ = this.fragQuery.device$;
    public apiUrl$ = this.fragQuery.timelineSieveSizesAtPercentsUrl$;
    public readonly token$ = this.fragQuery.apiToken$;
    public readonly duration$ = this.fragQuery.duration$;
    public isLoading: boolean = false;
    public fileType: string;
    private deviceSubscription: Subscription;
    private typeSubscription: Subscription;
    private durationSubscription: Subscription;

    public readonly type$ = this.route$.pipe(op.map((route) => route[0]));

    async openCSVDialog() {
        if (this.fileType === 'shift-analytics') {
            this.dialog.open(DialogCsvOptionsComponent, {
                width: 'auto',
            });
        } else if (this.fileType === 'muckpiles') {
            this.isLoading = true;
            const data = await firstValueFrom(this.fragQuery.muckpileData$.pipe(op.filter(d => d !== undefined)));
            const muckpile = await firstValueFrom(this.fragQuery.muckpile$.pipe(op.filter(m => m !== undefined)));
            await this.csvDownloadService.downloadMuckpileCsv(data, muckpile);
            this.isLoading = false;
        } else {
            this.isLoading = true;
            this.showMonthlyCsvMonths = false;
            this.selectedMonthlyCsvMonth = null;
            this.hasCurrentMonthCsv = false;
            this.monthlyCsvMonths = [];
            try {
                const device = await firstValueFrom(this.device$);
                this.monthlyCsvMonths = await this.csvDownloadService.getMonthlyReportMonths(device.id);
                this.hasCurrentMonthCsv = this.hasCurrentMonthReport(this.monthlyCsvMonths);
                this.showMonthlyCsvMonths = true;
            } catch (error) {
                this.snackBar.open('Failed to load monthly CSV reports.', 'OK', {
                    duration: 5000,
                });
            } finally {
                this.isLoading = false;
            }
        }

    }

    private getCurrentMonthKey(date = new Date()) {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }

    private hasCurrentMonthReport(months: MonthlyReportMonthOption[]) {
        return months.some(month =>
            month.month === this.getCurrentMonthKey()
            && month.status === 'ready'
        );
    }

    async downloadMonthlyCsv(month: string) {
        if (!month) {
            return;
        }
        this.isLoading = true;
        try {
            const device = await firstValueFrom(this.device$);
            await this.csvDownloadService.downloadMonthlyReportCsv(device.id, this.deviceName, month);
        } finally {
            this.isLoading = false;
        }
    }

    async reportExport() {
        const device = await firstValueFrom(this.device$);
        if (this.fragQuery.exportReport) {
            let url = '/report/data-interval#';
            if (this.fileType === 'shift-analytics') {
                url = '/report/time-interval#';
            } else if (this.fileType === 'muckpiles') {
                url = '/report/muckpile#';
            }

            if (this.fileType === 'muckpiles') {
                const muckpile = await firstValueFrom(this.fragQuery.muckpile$);
                url += JSON.stringify({
                    muckpileId: muckpile.id,
                    device: device.id,
                });
            } else {
               const fragDuration = await firstValueFrom(this.duration$);
                const duration =
                    fragDuration === 'custom'
                        ? [this.begin$.value, this.end$.value]
                        : fragDuration;
                const customDuration =
                    fragDuration === 'custom'
                        ? [this.begin$.value, this.end$.value]
                        : '';
                const label = fragDuration === 'custom' ? 'custom' : 'duration';
                const custom = fragDuration === 'custom' ? 'duration' : null;
                url += JSON.stringify({
                    device: device.id,
                    [label]: duration,
                    [custom]: customDuration,
                });
            }
            window.open(url, '_blank');
        }
        this.fragQuery.exportReport = false;
        this.host.close();
    }

    ngOnDestroy(): void {
        if (this.deviceSubscription) {
            this.deviceSubscription.unsubscribe();
        }
        if (this.typeSubscription) {
            this.typeSubscription.unsubscribe();
        }
        if (this.durationSubscription) {
            this.durationSubscription.unsubscribe();
        }
    }
    
    ngAfterViewInit(): void {
        this.route$.pipe(take(1)).subscribe(route => {
            if (route && route[0] === 'muckpiles') {
                this.apiUrl$ = this.fragQuery.muckpileApiUrl$;
            } else {
                this.apiUrl$ = this.fragQuery.timelineSieveSizesAtPercentsUrl$;
            }
        });
    }

    cancel() {
        this.host.close(false);
    }
}
